/**
 * Language Test Modal — Guided Stepper Flow
 *
 * 4-step wizard: Instructions -> Take Test -> Upload Screenshot -> Confirm & Submit
 *
 * Features:
 *  - Step indicator bar with animated transitions
 *  - Image preview before upload
 *  - CEFR extraction via the brain (image, PDF or DOCX)
 *  - A failed analysis is a FAILED state with its reason and a re-upload —
 *    never a completed test (H15). It used to be swallowed ("non-blocking")
 *    and submitted as done with no level, which the card rendered as a
 *    green "Completed" and the server recorded as `languageTestsCompleted`.
 *  - Back navigation between steps
 *
 * Refactored: step UI components extracted into StepIndicator,
 * InstructionsStep, TakeTestStep, UploadStep, ConfirmStep.
 */

import { LANGUAGE_CERTIFICATE_ACCEPT, type LanguageTestLanguage } from '@tonnie/heroes';
import { useTranslation } from '@tonnie/locale';
import { useUploadFile, useUploadViaProxy, useAnalyzeLanguageTest } from '@tonnie/sdk';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tonnie/ui';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Upload, X } from '@tonnie/assets';
import { useErrorCapture } from '@tonnie/observability';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useCallback, useRef, useState } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

import {
  type ILanguageTestConfig,
  type LanguageTestStep,
  type AnalysisState,
  STEP_ORDER,
  primaryCefrLevel,
} from './language-test.types';

import { StepIndicator } from './StepIndicator';
import { InstructionsStep } from './InstructionsStep';
import { TakeTestStep } from './TakeTestStep';
import { UploadStep } from './UploadStep';
import { ConfirmStep } from './ConfirmStep';

// ── Main Component ─────────────────────────────────────────────────────

export function LanguageTestModal({
  testConfig,
  talentId,
  language,
  onClose,
  onComplete,
}: {
  testConfig: ILanguageTestConfig;
  talentId?: string;
  /**
   * The language this test certifies (the widget config's `language`).
   * Required and sent as-is: it is never inferred from the test's upload
   * type, and never defaulted to English (H16).
   */
  language: LanguageTestLanguage;
  onClose: () => void;
  /** Called only with the level the certificate was assessed at — never without one. */
  onComplete: (cefrLevel: string) => void;
}) {
  const { t } = useTranslation();
  const { captureException } = useErrorCapture();
  // Persist the user's position in the wizard + whether they've opened
  // the external test link. File handles, image previews, and submit
  // state stay local — File objects can't be serialised and network
  // state shouldn't survive a reload.
  //
  // Scope by `<language>::<testConfig.id>` so the wizard step survives
  // ONLY for the specific test that owns it. Without the scope, all
  // language tests across both English and Portuguese share the same
  // `currentStep` slot, so opening Portuguese-Reading after closing
  // English-Reading would land on whichever step English was at —
  // exactly the cross-test leak that caused the "PT appeared as
  // Filled" symptom in the 2026-04-28 logs.
  const persistKey = `${language}::${testConfig.id}`;
  const [persisted, patchPersisted] = useWidgetState<{
    currentStep: LanguageTestStep;
    hasOpenedTest: boolean;
  }>(
    'language_test_modal',
    { currentStep: 'instructions', hasOpenedTest: false },
    { keyScope: persistKey }
  );
  const [analysis, setAnalysis] = useState<AnalysisState>({ status: 'idle' });
  // The step persists across reloads; the analysis outcome does not (it is
  // local). A reload on 'confirm' would otherwise show a confirm step with no
  // outcome at all — which used to offer Submit and complete the test with no
  // level. The confirm step exists only for an outcome.
  const currentStep: LanguageTestStep =
    persisted.currentStep === 'confirm' &&
    analysis.status !== 'analyzed' &&
    analysis.status !== 'failed'
      ? 'upload'
      : persisted.currentStep;
  const hasOpenedTest = persisted.hasOpenedTest;
  const setCurrentStep = useCallback(
    (next: LanguageTestStep) => patchPersisted({ currentStep: next }),
    [patchPersisted]
  );
  const setHasOpenedTest = useCallback(
    (next: boolean) => patchPersisted({ hasOpenedTest: next }),
    [patchPersisted]
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFileMutation = useUploadFile();
  const uploadProxyMutation = useUploadViaProxy();
  const analyzeTestMutation = useAnalyzeLanguageTest(talentId ?? '');

  const currentStepIdx = STEP_ORDER.indexOf(currentStep);

  const dialogWidth = typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.75, 960) : 960;
  const dialogHeight =
    typeof window !== 'undefined' ? Math.min(window.innerHeight * 0.8, 720) : 720;

  // ── Navigation ─────────────────────────────────────────────────────

  const goToStep = useCallback((step: LanguageTestStep) => setCurrentStep(step), []);

  const goNext = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    if (idx < STEP_ORDER.length - 1) setCurrentStep(STEP_ORDER[idx + 1]);
  }, [currentStep]);

  const goBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(currentStep);
    if (idx > 0) setCurrentStep(STEP_ORDER[idx - 1]);
  }, [currentStep]);

  // ── File handling ──────────────────────────────────────────────────

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Revoke previous preview URL
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    setSelectedFile(file);
    // A PDF or DOCX certificate has no inline preview — only images do.
    setImagePreviewUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
    setAnalysis({ status: 'idle' });
  }, []);

  const handleChangeFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  // ── Upload + Analyze ───────────────────────────────────────────────

  const handleUploadAndAnalyze = useCallback(async () => {
    if (!selectedFile) return;

    setAnalysis({ status: 'uploading' });

    let fileId: string;
    try {
      const fileType = selectedFile.name.split('.').pop() || 'png';

      // 1. Create vault file record
      ({ fileId } = await uploadFileMutation.mutateAsync({
        fileName: `language-test-${testConfig.id}-${Date.now()}.${fileType}`,
        fileType,
        category: 'checkup',
        subcategory: 'language_test',
        mimeType: selectedFile.type,
        fileSize: selectedFile.size,
      }));

      // 2. Upload via API proxy (avoids S3 CORS) — also auto-confirms
      await uploadProxyMutation.mutateAsync({
        fileId,
        data: selectedFile,
        contentType: selectedFile.type,
      });
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'LanguageTestModal',
        action: 'upload',
      });
      setAnalysis({ status: 'upload_error' });
      toast.error(t('checkup.languageTests.uploadFailed'));
      return;
    }

    // 3. Analyse. Every outcome is shown for what it is: a level, or a
    //    failure with its reason. Nothing here completes the test.
    setAnalysis({ status: 'analyzing', fileId });
    try {
      if (!talentId) throw new Error('No talent to analyse the certificate for');
      const result = await analyzeTestMutation.mutateAsync({
        fileId,
        testLanguage: language,
        testType: testConfig.id,
      });
      if (result.success) {
        const level = primaryCefrLevel(result.cefrResult);
        setAnalysis(
          level
            ? {
                status: 'analyzed',
                fileId,
                cefrResult: result.cefrResult,
                level,
                confidence: result.confidence,
              }
            : { status: 'failed', fileId, reason: 'no_cefr_found' }
        );
      } else {
        setAnalysis({ status: 'failed', fileId, reason: result.reason });
      }
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'LanguageTestModal',
        action: 'analyze',
      });
      setAnalysis({ status: 'failed', fileId, reason: 'request_failed' });
    }
    goToStep('confirm');
  }, [
    selectedFile,
    testConfig,
    talentId,
    uploadFileMutation,
    uploadProxyMutation,
    analyzeTestMutation,
    goToStep,
    t,
    captureException,
    language,
  ]);

  // ── Submit (final step) ────────────────────────────────────────────

  const handleSubmit = useCallback(() => {
    // Only an assessed level can be submitted — the button does not exist
    // for any other outcome, and this guard keeps it that way.
    if (analysis.status !== 'analyzed') return;
    const { level } = analysis;
    setIsSubmitted(true);
    toast.success(t('checkup.languageTests.uploadSuccess'));
    setTimeout(() => {
      onComplete(level);
      onClose();
    }, 1200);
  }, [analysis, onComplete, onClose, t]);

  const handleReUpload = useCallback(() => {
    setAnalysis({ status: 'idle' });
    goToStep('upload');
  }, [goToStep]);

  // ── Open test URL ──────────────────────────────────────────────────

  const handleOpenTestUrl = useCallback(() => {
    if (testConfig.testUrl) {
      window.open(testConfig.testUrl, '_blank');
      setHasOpenedTest(true);
    }
  }, [testConfig.testUrl]);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="p-0 flex flex-col rounded-xl max-w-none! border-border/50 shadow-lg"
        style={{
          width: `${dialogWidth}px`,
          height: `${dialogHeight}px`,
          maxWidth: 'none',
          maxHeight: 'none',
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{testConfig.name}</DialogTitle>
          <DialogDescription>{t('checkup.languageTests.modalDesc')}</DialogDescription>
        </DialogHeader>

        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 z-50 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background border border-border/50 shadow-sm"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Header with step indicator */}
        <div className="px-6 pt-5 pb-4 border-b border-border/50 bg-linear-to-r from-card to-card/95">
          <h2 className="text-lg font-bold text-foreground mb-4 pr-10">{testConfig.name}</h2>
          <StepIndicator currentIdx={currentStepIdx} />
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto bg-background">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {currentStep === 'instructions' && <InstructionsStep testConfig={testConfig} t={t} />}
              {currentStep === 'takeTest' && (
                <TakeTestStep
                  testConfig={testConfig}
                  hasOpenedTest={hasOpenedTest}
                  onOpenTest={handleOpenTestUrl}
                  t={t}
                />
              )}
              {currentStep === 'upload' && (
                <UploadStep
                  testConfig={testConfig}
                  selectedFile={selectedFile}
                  imagePreviewUrl={imagePreviewUrl}
                  analysis={analysis}
                  fileInputRef={fileInputRef}
                  onFileSelect={handleFileSelect}
                  onChangeFile={handleChangeFile}
                  onUploadAndAnalyze={handleUploadAndAnalyze}
                  t={t}
                />
              )}
              {currentStep === 'confirm' &&
                (analysis.status === 'analyzed' || analysis.status === 'failed') && (
                  <ConfirmStep
                    analysis={analysis}
                    imagePreviewUrl={imagePreviewUrl}
                    isSubmitted={isSubmitted}
                    t={t}
                  />
                )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer navigation — always visible, all action buttons here */}
        {!isSubmitted && (
          <div className="px-6 py-4 border-t border-border/50 bg-card flex items-center justify-between shrink-0">
            <div>
              {currentStepIdx > 0 && currentStep !== 'confirm' && (
                <Button variant="ghost" size="sm" onClick={goBack} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {t('common.back')}
                </Button>
              )}
              {currentStep === 'confirm' && (
                <Button
                  variant={analysis.status === 'failed' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={handleReUpload}
                  className="gap-2"
                  data-testid="widget-language_test-reupload"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('checkup.languageTests.reUpload')}
                </Button>
              )}
            </div>
            <div>
              {currentStep === 'instructions' && (
                <Button onClick={goNext} className="gap-2" data-testid="widget-language_test-next">
                  {t('checkup.languageTests.iUnderstand')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {currentStep === 'takeTest' && (
                <Button
                  onClick={goNext}
                  variant={hasOpenedTest ? 'default' : 'secondary'}
                  className="gap-2"
                  data-testid="widget-language_test-completed"
                >
                  {t('checkup.languageTests.completedTest')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {currentStep === 'upload' && selectedFile && (
                <Button
                  onClick={handleUploadAndAnalyze}
                  disabled={analysis.status === 'uploading' || analysis.status === 'analyzing'}
                  className="gap-2 min-w-[180px]"
                  data-testid="widget-language_test-analyze"
                >
                  {analysis.status === 'uploading' || analysis.status === 'analyzing' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {analysis.status === 'uploading'
                        ? t('checkup.languageTests.uploading')
                        : t('checkup.languageTests.analyzing')}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {t('checkup.languageTests.analyzeResult')}
                    </>
                  )}
                </Button>
              )}
              {currentStep === 'confirm' && analysis.status === 'analyzed' && (
                <Button
                  onClick={handleSubmit}
                  className="gap-2 min-w-[160px]"
                  data-testid="widget-language_test-submit"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t('checkup.languageTests.submitResult')}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={LANGUAGE_CERTIFICATE_ACCEPT}
          onChange={handleFileSelect}
          className="hidden"
        />
      </DialogContent>
    </Dialog>
  );
}
